import '../Dropdown/Dropdown.scss';

import React from 'react';
import type {
  DeselectOptionActionMeta,
  NamedProps as SelectProps,
  SelectOptionActionMeta,
  ValueType,
} from 'react-select';

import bem from '../../utils/bem';
import type { DropdownOption, DropdownOptionGroup } from '../Dropdown';
import type { DropdownTypes } from '../Dropdown/Dropdown';
import DropdownButton from '../Dropdown/DropdownButton';
import { useDropdown } from '../Dropdown/hooks';
import { useMultiSelectDropdown } from './hooks';
import MultiSelectContainer from './MultiSelectContainer';
import type {
  CLEAR_SELECTED_ITEMS_VALUE,
  SELECT_ALL_OPTIONS_VALUE,
} from './MultiSelectContainer/hooks/useMultiSelectContainer';

export type OptionTypeWithExtras<T = string> = T | typeof CLEAR_SELECTED_ITEMS_VALUE | typeof SELECT_ALL_OPTIONS_VALUE;

export type MultiSelectValue<T = string> = ValueType<DropdownOption<T>, true>;

export type MultiSelectOptions<T = string> = ReadonlyArray<DropdownOption<T> | DropdownOptionGroup<T>>;

type MultiSelectProps<T = string> = SelectProps<DropdownOption<T>, true, DropdownOptionGroup<T>>;

export interface MultiSelectDropdownProps<T = string>
  extends Omit<MultiSelectProps<OptionTypeWithExtras<T>>, 'onChange'> {
  readonly label?: string;
  readonly emptyDisplayText?: string;
  readonly multiOptionLabel?: string; // select-all option text - if omitted select all will not be an option in the dropdown.
  readonly multipleSelectControls?: boolean;
  readonly multipleSelectControlsInMenu?: boolean;
  readonly handleSelectAll?: () => void;
  readonly handleClearAll?: () => void;
  readonly value: MultiSelectValue<T>;
  readonly loading?: boolean;
  readonly loadingMore?: boolean;
  readonly error?: string;
  readonly isRemovable?: boolean;
  readonly onChange: (
    value: MultiSelectValue<T>,
    meta: SelectOptionActionMeta<DropdownOption> | DeselectOptionActionMeta<DropdownOption>
  ) => void;
  readonly options: MultiSelectOptions<T>;
  readonly errMsg?: string;
  readonly dataTestId?: string;
  readonly classNameModifier?: string;
  readonly onClose?: () => void;
  readonly handleRemove?: () => void;
  readonly type?: DropdownTypes;
  readonly onNext?: () => Promise<void>;
  readonly hasMore?: boolean;
  readonly setIsMultiSelectOpen?: (isOpen: boolean) => void;
  readonly multiControlRightSideBlock?: React.JSX.Element;
  // Enable a "loading" click-blocking modal
  readonly showSearchOverlay?: boolean;
  // Controls how the trigger text is formatted when items are selected.
  // 'labels' (default): comma-joined option labels. 'count': "N selected".
  readonly displayMode?: 'labels' | 'count';
}

// same styles as single-select dropdown
const [block, element] = bem('dropdown');

const defaultNoOptionsMessage = ({ inputValue }: { inputValue: string }): string =>
  inputValue ? `No results found for "${inputValue}"` : 'No options';

function MultiSelectDropdown<T = string>({
  label,
  classNameModifier,
  type,
  onClose,
  loading,
  loadingMore,
  isRemovable,
  errMsg,
  dataTestId,
  handleRemove,
  setIsMultiSelectOpen,
  noOptionsMessage,
  inputValue,
  showSearchOverlay,
  ...props
}: MultiSelectDropdownProps<T>): JSX.Element {
  const { active, setActive, focusRef, dropdownRef, handleKeyDown } = useDropdown({ onClose });
  const { isOptionSelected, handleChange, getDisplayValue } = useMultiSelectDropdown<T>(props);
  const searchOverlayRef = React.useRef<HTMLDivElement>(null);

  const modifiers = [
    ...(classNameModifier ? [classNameModifier] : []),
    ...(type ? [type] : []),
    ...(active ? ['active'] : []),
    ...(errMsg ? ['val-error'] : []),
  ];

  React.useEffect(() => {
    setIsMultiSelectOpen?.(active);
  }, [active, setIsMultiSelectOpen]);

  const isFetching = loading && !loadingMore;
  const hasOptions = props.options.length > 0;

  // Show "Loading…" only on the first fetch with no stale options and no active search.
  const isInitialLoading = isFetching && !hasOptions && !inputValue;

  // Opt-in overlay: dims stale options while a search fetch is in flight.
  const isSearchLoading = Boolean(showSearchOverlay && isFetching && inputValue);

  // Align overlay to the menu using getBoundingClientRect (menu's offsetParent
  // differs from the overlay's parent due to react-select internals).
  // `active` is included because the overlay only mounts when the dropdown is
  // open; without it the effect won't re-run when the ref first becomes non-null.
  React.useLayoutEffect(() => {
    if (!isSearchLoading || !searchOverlayRef.current) return;
    const container = searchOverlayRef.current.parentElement;
    if (!container) return;
    const menu = container.querySelector('.dropdown-list__menu');
    if (menu) {
      const containerRect = container.getBoundingClientRect();
      const menuRect = menu.getBoundingClientRect();
      searchOverlayRef.current.style.top = `${menuRect.top - containerRect.top}px`;
      searchOverlayRef.current.style.height = `${menuRect.height}px`;
    }
  }, [isSearchLoading, active]);

  return (
    <div className={block(modifiers)} ref={dropdownRef} data-testid="multi-dropdown-button">
      <DropdownButton
        label={label}
        emptyDisplayText={props.emptyDisplayText}
        displayValue={getDisplayValue(props.value)}
        active={active}
        isRemovable={isRemovable}
        setActive={setActive}
        isDisabled={props.isDisabled}
        dataTestId={dataTestId || 'dropdown-button'}
        onClose={onClose}
        handleRemove={handleRemove}
        errMsg={errMsg}
      />
      <div
        className={element('container', [
          props.isSearchable === false ? 'not-searchable' : null,
          props.menuPlacement === 'top' ? 'flipped' : null,
        ])}
        ref={focusRef}
        onKeyDown={handleKeyDown}
      >
        {active && !isInitialLoading && !props.error && (
          <MultiSelectContainer
            {...props}
            inputValue={inputValue}
            isOptionSelected={isOptionSelected}
            onChange={handleChange}
            noOptionsMessage={noOptionsMessage ?? defaultNoOptionsMessage}
          />
        )}
        {active && isSearchLoading && (
          <div ref={searchOverlayRef} className={element('search-overlay')} data-testid="search-overlay">
            <span className={element('search-overlay-text')}>Loading&hellip;</span>
          </div>
        )}
        {active && isInitialLoading && <div className={element('loading')}>Loading...</div>}
        {active && props.error && (
          <div className={element('error')}>Something went wrong. Refresh the page or try again later.</div>
        )}
      </div>
    </div>
  );
}

export default MultiSelectDropdown;
